import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { settingsApi } from '@services/api';
import { LoadingSpinner, EmptyState } from '@components/common';
import type { Settings } from '@types';

const inputClass =
  'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500';
const labelClass = 'block text-sm font-medium text-gray-700';

/** Owner-editable defaults (F9.1). */
export default function SettingsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
  });

  const { register, handleSubmit, reset } = useForm<Settings>();

  useEffect(() => {
    if (data) reset(data);
  }, [data, reset]);

  const mutation = useMutation({
    mutationFn: (values: Settings) => settingsApi.update(values),
    onSuccess: updated => {
      queryClient.setQueryData(['settings'], updated);
      toast.success('Settings saved');
    },
    onError: () => toast.error('Could not save settings'),
  });

  if (isLoading) {
    return (
      <div data-testid="settings-loading" className="flex justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div data-testid="settings-error">
        <EmptyState
          title="Couldn't load settings"
          description="Something went wrong. Please try again."
          action={{ label: 'Retry', onClick: () => refetch() }}
        />
      </div>
    );
  }

  const onSubmit = (values: Settings) =>
    mutation.mutate({
      ...values,
      default_due_days: Number(values.default_due_days),
      default_validity_days: Number(values.default_validity_days),
    });

  return (
    <div data-testid="settings-page">
      <h1 className="mb-6 text-2xl font-bold text-gray-900">Settings</h1>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="max-w-2xl space-y-6"
        data-testid="settings-form"
      >
        <div className="grid grid-cols-1 gap-4 rounded-lg border border-gray-200 bg-white p-5 shadow-sm sm:grid-cols-2">
          <div>
            <label className={labelClass}>Default due days (invoices)</label>
            <input
              type="number"
              min={0}
              data-testid="setting-due-days"
              className={inputClass}
              {...register('default_due_days')}
            />
          </div>
          <div>
            <label className={labelClass}>Default validity days (quotes)</label>
            <input
              type="number"
              min={0}
              data-testid="setting-validity-days"
              className={inputClass}
              {...register('default_validity_days')}
            />
          </div>
        </div>

        <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div>
            <label className={labelClass}>Default terms</label>
            <textarea
              rows={3}
              data-testid="setting-terms"
              className={inputClass}
              {...register('default_terms')}
            />
          </div>
          <div>
            <label className={labelClass}>Default acceptance line</label>
            <input
              data-testid="setting-accept-line"
              className={inputClass}
              {...register('default_accept_line')}
            />
          </div>
          <div>
            <label className={labelClass}>Default prepared-by label</label>
            <input
              data-testid="setting-prepared-label"
              className={inputClass}
              {...register('default_prepared_label')}
            />
          </div>
          <div>
            <label className={labelClass}>Business contact block</label>
            <textarea
              rows={4}
              data-testid="setting-business-block"
              className={inputClass}
              {...register('business_contact_block')}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            data-testid="settings-save"
            disabled={mutation.isPending}
            className="rounded-md bg-primary-600 px-5 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
